import type {Location, WeatherStationFromList} from '../../api/types';
import {isStationHidden, isStationRetired} from '../Station';
import {USER_LOCATION_HASH} from './constants';

/**
 * The My weather list (FE #381): the user's stations and places in one list,
 * in four sections. Platform-free on purpose: each app draws the rows.
 */

// The order tokens' shape (`kind:id`), as in ./index.ts. Repeated rather
// than imported: index.ts re-exports this module.
const stationKey = (id: number) => `station:${id}`;
const placeKey = (hash: string) => `place:${hash}`;

export type MyWeatherStationItem = {
  kind: 'station';
  /** The station's order token, `station:<id>`: unique in a section. */
  key: string;
  station: WeatherStationFromList;
};

export type MyWeatherPlaceItem = {
  kind: 'place';
  /** The place's order token, `place:<hash>`: unique in a section. */
  key: string;
  place: Location;
};

export type MyWeatherItem = MyWeatherStationItem | MyWeatherPlaceItem;

export type MyWeatherSection =
  /** The stations the user owns (linked to their account), not hidden. */
  | {id: 'myStations'; items: MyWeatherStationItem[]}
  /**
   * Always present: the row is the way to a position. It carries nothing:
   * each app reads the GPS entry itself, so a position update does not
   * rebuild the sections.
   */
  | {id: 'currentLocation'}
  /** Favourite stations the user does not own, and favourite places. */
  | {id: 'favorites'; items: MyWeatherItem[]}
  /** Stations and places viewed but not kept. */
  | {id: 'recent'; items: MyWeatherItem[]};

export type MyWeatherSections = {
  /** In display order; an empty section is left out, Current location never. */
  sections: MyWeatherSection[];
};

export type MyWeatherSectionsInput = {
  /** Every station the device holds, in the user's station order. */
  stations: WeatherStationFromList[];
  favoriteStationIds: number[];
  /** The favourite places, in their favourite order. */
  favoritePlaces: Location[];
  /** The places viewed but not kept, in list order. */
  recentPlaces: Location[];
  /**
   * The account's My weather order (`kind:id` tokens, places by device
   * hash), null until the account has one.
   */
  order: string[] | null;
};

const stationItem = (
  station: WeatherStationFromList,
): MyWeatherStationItem => ({
  kind: 'station',
  key: stationKey(station.id),
  station,
});

const placeItem = (place: Location): MyWeatherPlaceItem => ({
  kind: 'place',
  key: placeKey(place.hash),
  place,
});

const notGps = (place: Location) => place.hash !== USER_LOCATION_HASH;

/**
 * Favourites in the My weather order. A token naming nothing here (a
 * station owned, so in My stations; a place removed; another device's
 * entry) is dropped. What the order lacks follows it: stations in station
 * order, then places in favourite order.
 */
const inOrder = (
  stations: MyWeatherItem[],
  places: MyWeatherItem[],
  order: string[] | null,
): MyWeatherItem[] => {
  const byKey = new Map(
    [...stations, ...places].map((item) => [item.key, item]),
  );
  const placed = new Set<string>();
  const items: MyWeatherItem[] = [];
  for (const token of order ?? []) {
    const item = byKey.get(token);
    if (item && !placed.has(token)) {
      placed.add(token);
      items.push(item);
    }
  }
  for (const item of [...stations, ...places]) {
    if (!placed.has(item.key)) {
      placed.add(item.key);
      items.push(item);
    }
  }
  return items;
};

export const buildSections = ({
  stations,
  favoriteStationIds,
  favoritePlaces,
  recentPlaces,
  order,
}: MyWeatherSectionsInput): MyWeatherSections => {
  const isFavorite = (station: WeatherStationFromList) =>
    favoriteStationIds.includes(station.id);
  // Owned wins over favourite: a station appears once.
  const myStations = stations
    .filter((s) => Boolean(s.link) && !isStationHidden(s))
    .map(stationItem);
  const favorites = inOrder(
    stations.filter((s) => !s.link && isFavorite(s)).map(stationItem),
    favoritePlaces.filter(notGps).map(placeItem),
    order,
  );
  const recent: MyWeatherItem[] = [
    ...stations.filter((s) => !s.link && !isFavorite(s)).map(stationItem),
    ...recentPlaces.filter(notGps).map(placeItem),
  ];

  const sections: MyWeatherSection[] = [];
  if (myStations.length > 0) {
    sections.push({id: 'myStations', items: myStations});
  }
  sections.push({id: 'currentLocation'});
  if (favorites.length > 0) {
    sections.push({id: 'favorites', items: favorites});
  }
  if (recent.length > 0) {
    sections.push({id: 'recent', items: recent});
  }
  return {sections};
};

/**
 * The My weather editor's groups (FE #381 step 3). Not the list's sections:
 * your stations reorder among themselves and never mix into Favorites; there
 * is no Current location and no Recent, which hold nothing to edit.
 */
export type MyWeatherEditorSections = {
  /** Your stations, not hidden, still reporting: they reorder among themselves. */
  myStations: MyWeatherStationItem[];
  /**
   * Yours, gone from the provider's listing (#324): always sorted after the
   * live ones, so they keep their place.
   */
  retired: MyWeatherStationItem[];
  /** As in the list: favourite stations and places in one order. */
  favorites: MyWeatherItem[];
  /** Yours, hidden: listed to show them again. */
  hidden: MyWeatherStationItem[];
};

export type MyWeatherEditorInput = Omit<MyWeatherSectionsInput, 'recentPlaces'>;

export const buildEditorSections = ({
  stations,
  favoriteStationIds,
  favoritePlaces,
  order,
}: MyWeatherEditorInput): MyWeatherEditorSections => {
  const linked = stations.filter((s) => Boolean(s.link));
  const shown = linked.filter((s) => !isStationHidden(s));
  return {
    myStations: shown.filter((s) => !isStationRetired(s)).map(stationItem),
    retired: shown.filter(isStationRetired).map(stationItem),
    favorites: inOrder(
      stations
        .filter((s) => !s.link && favoriteStationIds.includes(s.id))
        .map(stationItem),
      favoritePlaces.filter(notGps).map(placeItem),
      order,
    ),
    hidden: linked.filter(isStationHidden).map(stationItem),
  };
};

/**
 * The places of these sections, in list order, once each: what a list asks
 * the current hour of. Each app passes the sections it draws (the webapp
 * has no Recent).
 */
export const sectionPlaces = (sections: MyWeatherSection[]): Location[] => {
  const seen = new Set<string>();
  const places: Location[] = [];
  sections.forEach((section) => {
    const items: MyWeatherItem[] = 'items' in section ? section.items : [];
    items.forEach((item) => {
      if (item.kind === 'place' && !seen.has(item.place.hash)) {
        seen.add(item.place.hash);
        places.push(item.place);
      }
    });
  });
  return places;
};
